/**
 * Opmaak-editor voor Algemene opmerkingen en de crew-briefing (wiki-feedback Callsheets 26 sep:
 * "Kan dit in WYSIWYG editor dat je beter kan aanpassen"). Hergebruikt NotitieEditor uit
 * Notities (TipTap + markdown-opslag), zonder afbeeldingen: deze tekst gaat naar Airtable,
 * de PDF en de crew-app.
 *
 * Module-niveau componenten: binnen CallSheetManager gedefinieerd zou elke render een nieuwe
 * component-identiteit geven en de editor laten remounten (zie EditableField).
 */
import React, { useMemo, useRef, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ReactMarkdown from 'react-markdown';
import NotitieEditor from '../../ui-components/NotitieEditor';
import { mdLinkComponents } from '../../utils/briefingBronnen';
import { metRegeleinden, naarEditor, paginaLinkRegister, uitEditor } from './callsheetMarkdown';

export const MD_WEERGAVE_SX = {
  '& h1, & h2, & h3': { fontSize: '0.95rem', fontWeight: 800, mt: 2, mb: 0.5 },
  '& > :first-of-type': { mt: 0 },
  '& ul, & ol': { pl: 2.5, my: 0.5 },
  '& li': { mb: 0.35, lineHeight: 1.55, fontSize: '0.9rem' },
  '& p': { lineHeight: 1.6, fontSize: '0.9rem', my: 0.5 },
  '& a': { color: '#4338ca', wordBreak: 'break-all' },
};

/** Editor met schone markdown-opslag. `value`/`onChange` zijn de opgeslagen tekst. */
export default function CallsheetTekstEditor({ value, onChange, placeholder, minHeight = 140, autoFocus = false }) {
  // Gedeeld linkregister voor de hele pagina (zie callsheetMarkdown): een token dat je naar een
  // ander veld kopieert, wijst daar naar dezelfde link.
  const inEditor = useMemo(() => naarEditor(value, paginaLinkRegister), [value]);
  return (
    <NotitieEditor value={inEditor} onChange={(md) => onChange?.(uitEditor(md, paginaLinkRegister))}
      placeholder={placeholder} minHeight={minHeight} autoFocus={autoFocus} afbeeldingen={false} />
  );
}

/** Opgemaakte weergave van opgeslagen markdown (Enter = nieuwe regel, zoals in de editor). */
export function OpmaakWeergave({ tekst }) {
  return (
    <Box sx={MD_WEERGAVE_SX}>
      <ReactMarkdown components={mdLinkComponents}>{metRegeleinden(tekst)}</ReactMarkdown>
    </Box>
  );
}

/**
 * Callsheetveld met opmaak: lezen als opgemaakte tekst, potloodje → editor.
 * Opslaan bij wegklikken (zoals de andere velden) of met de knop; Annuleren gooit weg.
 */
export function OpmaakVeld({ value, onSave, placeholder }) {
  const [bewerk, setBewerk] = useState(false);
  const [concept, setConcept] = useState('');
  const klaarRef = useRef(false);

  const open = () => { klaarRef.current = false; setConcept(value || ''); setBewerk(true); };
  const sluit = (bewaren) => {
    if (klaarRef.current) return;
    klaarRef.current = true;
    setBewerk(false);
    if (bewaren && concept !== (value || '')) onSave(concept);
  };

  if (!bewerk) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {value ? <OpmaakWeergave tekst={value} /> : <Typography variant="body2" color="text.secondary">-</Typography>}
        </Box>
        <IconButton size="small" aria-label="Bewerken" onClick={open} sx={{ opacity: 0.5, '&:hover': { opacity: 1 } }}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      // Klikken op de werkbalk, randen of knoppen houdt de focus in de editor; alleen echt
      // wegklikken (buiten dit blok) slaat op.
      onMouseDown={(e) => { if (!e.target.closest?.('.ProseMirror')) e.preventDefault(); }}
      onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) sluit(true); }}
      onKeyDown={(e) => { if (e.key === 'Escape') { e.preventDefault(); sluit(false); } }}
    >
      <CallsheetTekstEditor value={concept} onChange={setConcept} placeholder={placeholder} autoFocus />
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto' }}>
          Opgeslagen zodra je hier wegklikt · Esc = annuleren
        </Typography>
        <Button size="small" onClick={() => sluit(false)} sx={{ textTransform: 'none' }}>Annuleren</Button>
        <Button size="small" variant="contained" disableElevation onClick={() => sluit(true)}
          sx={{ textTransform: 'none', fontWeight: 700 }}>Opslaan</Button>
      </Box>
    </Box>
  );
}
